// ---------------------------------------------------------------------------
// Test Mode — raportul financiar simulat (Financiar → Rapoarte).
//
// Pagina de rapoarte nu avea niciun mock, deci nu putea fi deschisă în modul
// demo. Builder-ul de aici întoarce exact forma pe care o trimite
// `GET /api/financials/report`, pentru orice perioadă: sumele se scalează cu
// numărul de luni al intervalului, iar bucket-urile de tendință urmează
// granularitatea perioadei.
//
// Două cazuri speciale, pentru verificat în browser:
//   • luna 2026-01 întoarce un snapshot ÎNCHIS cu `reportVersion: 1` și fără
//     secțiunile Oameni / Clienți — exact ca o lună închisă înainte de ele;
//   • orice lună din viitor rămâne deschisă, deci „Închide luna" e dezactivat.
// ---------------------------------------------------------------------------

import type {
  ClientsSection,
  ClosingListItem,
  MonthlyReport,
  MonthlyReportResponse,
  PeopleSection,
} from '../lib/monthlyReportApi';
import {
  dayKey,
  lastDayOfMonth,
  periodKey,
  quarterOf,
  shiftMonth,
  type ReportCompareMode,
  type ReportPeriodKind,
  type ReportPeriodState,
} from '../lib/reportPeriod';

/** Luna al cărei snapshot e „vechi" (fără Oameni / Clienți). */
const STALE_SNAPSHOT_MONTH = '2026-01';

const MONTHLY_INFLOWS = 210_000;
const MONTHLY_OUTFLOWS = 124_000;
const MONTHLY_PAYROLL = 76_500;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function dayFromKey(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Query → starea de perioadă, cu aceleași reguli ca pagina. */
export function reportPeriodFromQuery(query: URLSearchParams): ReportPeriodState {
  const kindRaw = query.get('kind');
  const kinds: ReportPeriodKind[] = ['month', 'quarter', 'year', 'custom'];
  const kind = kinds.includes(kindRaw as ReportPeriodKind)
    ? (kindRaw as ReportPeriodKind)
    : 'month';
  const year = Number(query.get('year')) || 2026;
  const month = Number(query.get('month')) || 3;
  const quarter = Number(query.get('quarter')) || quarterOf(month);
  const from = query.get('from') ?? dayKey(year, month, 1);
  const to = query.get('to') ?? dayKey(year, month, lastDayOfMonth(year, month));
  const compare: ReportCompareMode = query.get('compare') === 'yoy' ? 'yoy' : 'prev';
  return { kind, year, month, quarter, from, to, compare };
}

interface ResolvedPeriod {
  key: string;
  kind: ReportPeriodKind;
  from: Date;
  to: Date;
  months: Array<{ year: number; month: number }>;
  monthsCount: number;
  compareKey: string;
}

function resolve(state: ReportPeriodState): ResolvedPeriod {
  const key = periodKey(state);
  const monthsOf = (first: { year: number; month: number }, count: number) =>
    Array.from({ length: count }, (_, i) => shiftMonth(first.year, first.month, i));
  switch (state.kind) {
    case 'month': {
      const prev = shiftMonth(state.year, state.month, state.compare === 'yoy' ? -12 : -1);
      return {
        key,
        kind: 'month',
        from: new Date(Date.UTC(state.year, state.month - 1, 1)),
        to: new Date(Date.UTC(state.year, state.month, 0, 23, 59, 59, 999)),
        months: [{ year: state.year, month: state.month }],
        monthsCount: 1,
        compareKey: `${prev.year}-${String(prev.month).padStart(2, '0')}`,
      };
    }
    case 'quarter': {
      const first = (state.quarter - 1) * 3 + 1;
      const prevQ =
        state.compare === 'yoy' ? state.quarter : state.quarter === 1 ? 4 : state.quarter - 1;
      const prevY =
        state.compare === 'yoy'
          ? state.year - 1
          : state.quarter === 1
            ? state.year - 1
            : state.year;
      return {
        key,
        kind: 'quarter',
        from: new Date(Date.UTC(state.year, first - 1, 1)),
        to: new Date(Date.UTC(state.year, first + 2, 0, 23, 59, 59, 999)),
        months: monthsOf({ year: state.year, month: first }, 3),
        monthsCount: 3,
        compareKey: `${prevY}-Q${prevQ}`,
      };
    }
    case 'year':
      return {
        key,
        kind: 'year',
        from: new Date(Date.UTC(state.year, 0, 1)),
        to: new Date(Date.UTC(state.year, 12, 0, 23, 59, 59, 999)),
        months: monthsOf({ year: state.year, month: 1 }, 12),
        monthsCount: 12,
        compareKey: String(state.year - 1),
      };
    case 'custom': {
      const from = dayFromKey(state.from);
      const toDay = dayFromKey(state.to);
      const to = new Date(toDay.getTime() + 86_399_999);
      const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1);
      const months: Array<{ year: number; month: number }> = [];
      let cursor = { year: from.getUTCFullYear(), month: from.getUTCMonth() + 1 };
      const lastIdx = toDay.getUTCFullYear() * 12 + toDay.getUTCMonth();
      for (;;) {
        months.push(cursor);
        if (cursor.year * 12 + (cursor.month - 1) >= lastIdx) break;
        cursor = shiftMonth(cursor.year, cursor.month, 1);
      }
      return {
        key,
        kind: 'custom',
        from,
        to,
        months,
        monthsCount: round2(days / 30.44),
        compareKey: `${state.from}..${state.to}`,
      };
    }
  }
}

/** Bucket-urile graficului, în aceeași granularitate ca perioada. */
function buckets(p: ResolvedPeriod): string[] {
  if (p.kind === 'year') {
    const y = p.months[0].year;
    return [y - 4, y - 3, y - 2, y - 1, y].map(String);
  }
  if (p.kind === 'quarter') {
    const first = p.months[0];
    const out: string[] = [];
    for (let i = 7; i >= 0; i -= 1) {
      const m = shiftMonth(first.year, first.month, -i * 3);
      out.push(`${m.year}-Q${quarterOf(m.month)}`);
    }
    return out;
  }
  const last = p.months[p.months.length - 1];
  const out: string[] = [];
  for (let i = 11; i >= 0; i -= 1) {
    const m = shiftMonth(last.year, last.month, -i);
    out.push(`${m.year}-${String(m.month).padStart(2, '0')}`);
  }
  return out;
}

function buildPeople(
  scale: number,
  inflows: number,
  outflows: number,
  keys: string[],
): PeopleSection {
  const totalCost = round2(MONTHLY_PAYROLL * scale);
  const rows = [
    { personId: 'p-ana', name: 'Ana Pop', departmentName: 'Dezvoltare', weight: 0.34 },
    { personId: 'p-bogdan', name: 'Bogdan Ionescu', departmentName: 'Dezvoltare', weight: 0.3 },
    { personId: 'p-carmen', name: 'Carmen Dima', departmentName: 'Design', weight: 0.22 },
    { personId: 'p-dan', name: 'Dan Marin', departmentName: 'Vânzări', weight: 0.14 },
  ].map((r, i) => {
    const cost = round2(totalCost * r.weight);
    const hours = Math.round(150 * scale) - i * 6;
    return {
      personId: r.personId,
      name: r.name,
      departmentName: r.departmentName,
      totalCost: cost,
      grossSalary: round2(cost * 0.78),
      netSalary: round2(cost * 0.46),
      employeeContributions: round2(cost * 0.27),
      incomeTax: round2(cost * 0.05),
      employerCam: round2(cost * 0.022),
      hours,
      costPerHour: hours > 0 ? round2(cost / hours) : null,
      leaveDays: i === 2 ? 3 : 0,
      monthsWithPayroll: Math.max(1, Math.round(scale)),
      share: round2(r.weight * 100),
    };
  });
  const byDept = new Map<string, { amount: number; count: number }>();
  for (const r of rows) {
    const e = byDept.get(r.departmentName ?? '—') ?? { amount: 0, count: 0 };
    e.amount += r.totalCost;
    e.count += 1;
    byDept.set(r.departmentName ?? '—', e);
  }
  // Contribuțiile se virează luna următoare, deci banca a plătit mai puțin.
  const ledgerPaid = round2(totalCost * 0.72);
  return {
    totalCost,
    breakdown: {
      gross: round2(totalCost * 0.78),
      contributions: round2(totalCost * 0.27),
      incomeTax: round2(totalCost * 0.05),
      cam: round2(totalCost * 0.022),
      net: round2(totalCost * 0.46),
    },
    previousTotalCost: round2(totalCost * 0.94),
    rows,
    byDepartment: [...byDept.entries()]
      .map(([name, v]) => ({
        name,
        amount: round2(v.amount),
        count: v.count,
        share: round2((v.amount / totalCost) * 100),
      }))
      .sort((a, b) => b.amount - a.amount),
    headcount: rows.length,
    monthsCovered: Math.max(1, Math.round(scale)),
    shareOfInflows: round2((totalCost / inflows) * 100),
    shareOfOutflows: round2((totalCost / outflows) * 100),
    revenuePerPerson: round2(inflows / rows.length),
    returnPerLeu: round2(inflows / totalCost),
    hoursTotal: rows.reduce((s, r) => s + r.hours, 0) + 18,
    hoursUnbooked: 18,
    ledgerPaid,
    ledgerDividends: 0,
    ledgerVsPayroll: round2(ledgerPaid - totalCost),
    trend: keys.map((period, i) => ({
      period,
      totalCost: round2(MONTHLY_PAYROLL * scale * (0.82 + i * 0.02)),
      headcount: i < 4 ? 3 : 4,
    })),
    warnings: ['ledger_payroll_mismatch', 'hours_without_payroll'],
  };
}

function buildClients(scale: number, keys: string[]): ClientsSection {
  const invoicedTotal = round2(MONTHLY_INFLOWS * scale * 1.08);
  const seeds = [
    { key: 'cui:14399840', clientId: 'client-1', name: 'MARTINASYNC SRL', weight: 0.42, dso: 18 },
    { key: 'cui:40112233', clientId: 'client-2', name: 'Nordic Retail SRL', weight: 0.27, dso: 34 },
    { key: 'cui:44556677', clientId: null, name: 'Vertex Studio SRL', weight: 0.18, dso: 12 },
    { key: 'cui:48899001', clientId: null, name: 'Lumen Labs SRL', weight: 0.13, dso: 51 },
  ];
  const rows = seeds.map((s, i) => {
    const invoiced = round2(invoicedTotal * s.weight);
    return {
      clientId: s.clientId,
      key: s.key,
      name: s.name,
      invoiced,
      collected: round2(invoiced * (i === 3 ? 0.2 : 0.9)),
      open: round2(invoiced * (i === 3 ? 0.8 : 0.1)),
      overdue: i === 3 ? round2(invoiced * 0.8) : 0,
      invoiceCount: 4 - i,
      dso: s.dso,
      firstInvoiceAt: i === 2 ? '2026-03-04T00:00:00.000Z' : '2024-05-12T00:00:00.000Z',
      lastInvoiceAt: '2026-03-28T00:00:00.000Z',
      isNew: i === 2,
      share: round2(s.weight * 100),
    };
  });
  const overdueRows = rows.filter((r) => r.overdue > 0);
  const overdueTotal = overdueRows.reduce((s, r) => s + r.overdue, 0);
  return {
    rows,
    invoicedTotal,
    collectedTotal: round2(rows.reduce((s, r) => s + r.collected, 0)),
    concentration: {
      top1: round2(seeds[0].weight * 100),
      top3: round2((seeds[0].weight + seeds[1].weight + seeds[2].weight) * 100),
      hhi: round2(seeds.reduce((s, x) => s + Math.pow(x.weight * 100, 2), 0)),
    },
    newClients: rows.filter((r) => r.isNew),
    dormant: [
      {
        key: 'cui:39887766',
        name: 'Arcadia Media SRL',
        lastInvoiceAt: '2025-11-18T00:00:00.000Z',
        lastAmount: 24_800,
        daysSince: 131,
      },
    ],
    dso: 24,
    dsoPrevious: 29,
    overdueByClient: overdueRows.map((r) => ({
      name: r.name,
      amount: r.overdue,
      count: 1,
      share: round2((r.overdue / overdueTotal) * 100),
    })),
    trend: keys.map((period, i) => ({
      period,
      invoiced: round2(MONTHLY_INFLOWS * scale * (0.8 + i * 0.03)),
      collected: round2(MONTHLY_INFLOWS * scale * (0.74 + i * 0.03)),
      clients: 3 + (i % 3),
    })),
    otherCurrencies: [{ currency: 'EUR', amount: 2400 }],
  };
}

export function buildFinancialReport(query: URLSearchParams): MonthlyReportResponse {
  const state = reportPeriodFromQuery(query);
  const p = resolve(state);
  const scale = Math.max(0.2, p.monthsCount);
  const keys = buckets(p);

  const inflows = round2(MONTHLY_INFLOWS * scale);
  const outflows = round2(MONTHLY_OUTFLOWS * scale);
  const net = round2(inflows - outflows);
  const prevInflows = round2(inflows * 0.93);
  const prevOutflows = round2(outflows * 0.97);
  const stale = p.key === STALE_SNAPSHOT_MONTH;

  const categories = [
    { code: 'salarii', label: 'Salarii', weight: 0.52 },
    { code: 'subscriptii', label: 'Abonamente', weight: 0.11 },
    { code: 'chirii', label: 'Chirii', weight: 0.1 },
    { code: 'subcontractori', label: 'Subcontractori', weight: 0.14 },
    { code: 'contabilitate', label: 'Contabilitate', weight: 0.05 },
    { code: 'altele', label: 'Altele', weight: 0.08 },
  ].map((c) => ({
    code: c.code,
    label: c.label,
    amount: round2(outflows * c.weight),
    previous: round2(prevOutflows * c.weight),
    average3m: round2(outflows * c.weight * 0.96),
    share: round2(c.weight * 100),
  }));

  const report: MonthlyReport = {
    year: p.months[0].year,
    month: p.months[0].month,
    period: p.key,
    periodInfo: {
      kind: p.kind,
      key: p.key,
      from: p.from.toISOString(),
      to: p.to.toISOString(),
      months: p.months,
      monthsCount: p.monthsCount,
      compare: { mode: state.compare, key: p.compareKey },
    },
    reportVersion: stale ? 1 : 2,
    generatedAt: new Date().toISOString(),
    companyIds: null,
    summary: {
      currency: 'RON',
      inflows,
      outflows,
      net,
      previous: {
        inflows: prevInflows,
        outflows: prevOutflows,
        net: round2(prevInflows - prevOutflows),
      },
      change: {
        inflows: round2(((inflows - prevInflows) / prevInflows) * 100),
        outflows: round2(((outflows - prevOutflows) / prevOutflows) * 100),
        net: 12.4,
      },
      bankBalance: 318_400,
      bankBalanceAsOf: p.to.toISOString(),
      bankBalanceChange: 42_300,
      invoiced: round2(inflows * 1.08),
      collected: inflows,
      otherCurrencies: { inflows: [{ currency: 'EUR', amount: 2400 }], outflows: [] },
    },
    accounts: {
      items: [
        {
          key: 'acc-ing',
          bankName: 'ING Bank',
          label: 'Cont curent',
          iban: 'RO49INGB0000999900000001',
          currency: 'RON',
          openingBalance: 276_100,
          closingBalance: 318_400,
          closingAsOf: p.to.toISOString(),
          credits: inflows,
          debits: outflows,
          net,
          transactions: Math.round(48 * scale),
          unreconciled: 2,
          internalTransfers: 3,
          missingStatement: false,
          unfinalizedStatements: 0,
        },
        {
          key: 'acc-procredit',
          bankName: 'ProCredit Bank',
          label: 'Cont EUR',
          iban: 'RO12MIRO0000999900000002',
          currency: 'EUR',
          openingBalance: 4_120,
          closingBalance: 4_520,
          closingAsOf: p.to.toISOString(),
          credits: 2_400,
          debits: 2_000,
          net: 400,
          transactions: Math.round(6 * scale),
          unreconciled: 0,
          internalTransfers: 0,
          missingStatement: false,
          unfinalizedStatements: 0,
        },
      ],
      missingStatements: 0,
      unreconciled: 2,
    },
    revenue: {
      invoices: {
        count: Math.round(7 * scale),
        creditNotes: 1,
        net: round2(inflows * 0.9),
        vat: round2(inflows * 0.18),
        total: round2(inflows * 1.08),
      },
      collected: inflows,
      topClientsInvoiced: [
        { name: 'MARTINASYNC SRL', amount: round2(inflows * 0.45), count: 3, share: 42 },
        { name: 'Nordic Retail SRL', amount: round2(inflows * 0.29), count: 2, share: 27 },
        { name: 'Vertex Studio SRL', amount: round2(inflows * 0.19), count: 1, share: 18 },
      ],
      topClientsCollected: [
        { name: 'MARTINASYNC SRL', amount: round2(inflows * 0.44), count: 3, share: 44 },
        { name: 'Nordic Retail SRL', amount: round2(inflows * 0.28), count: 2, share: 28 },
      ],
      concentration: { name: 'MARTINASYNC SRL', share: 42 },
      otherCurrencies: [{ currency: 'EUR', amount: 2400 }],
    },
    expenses: {
      total: outflows,
      categories,
      topSuppliers: [
        { name: 'Hostinger', amount: round2(outflows * 0.04), count: 2, share: 4 },
        { name: 'Anthropic', amount: round2(outflows * 0.03), count: 1, share: 3 },
      ],
      subscriptions: [
        {
          id: 'sub-anthropic',
          name: 'Anthropic',
          frequency: 'monthly',
          expectedAmount: 450,
          expectedForPeriod: round2(450 * scale),
          expectedCurrency: 'RON',
          paidAmount: round2(450 * scale),
          paidCurrency: 'RON',
          payments: Math.round(scale) || 1,
          status: 'paid',
        },
        {
          id: 'sub-figma',
          name: 'Figma',
          frequency: 'monthly',
          expectedAmount: 220,
          expectedForPeriod: round2(220 * scale),
          expectedCurrency: 'RON',
          paidAmount: 0,
          paidCurrency: 'RON',
          payments: 0,
          status: 'missing',
        },
        {
          id: 'sub-domeniu',
          name: 'Domeniu firmio.ro',
          frequency: 'annual',
          expectedAmount: 90,
          expectedForPeriod: round2(90 * (scale / 12)),
          expectedCurrency: 'RON',
          paidAmount: 0,
          paidCurrency: 'RON',
          payments: 0,
          status: 'not_due',
        },
      ],
      subscriptionsTotal: round2(450 * scale),
      subscriptionsRunRateMonthly: 677.5,
      subscriptionsRunRateAnnual: 8130,
    },
    receivables: {
      total: 46_800,
      count: 3,
      buckets: [
        { key: 'current', count: 1, amount: 18_000 },
        { key: 'd1_30', count: 1, amount: 9_200 },
        { key: 'd31_60', count: 0, amount: 0 },
        { key: 'd61_90', count: 1, amount: 19_600 },
        { key: 'd90plus', count: 0, amount: 0 },
      ],
      topParty: { name: 'Lumen Labs SRL', amount: 19_600, count: 1, share: 42 },
      items: [
        {
          id: 'r-1',
          source: 'efactura',
          invoiceNumber: 'FIRMIO 0042',
          party: 'Lumen Labs SRL',
          amount: 19_600,
          currency: 'RON',
          issueDate: '2026-01-12T00:00:00.000Z',
          dueDate: '2026-01-27T00:00:00.000Z',
          daysOverdue: 63,
        },
        {
          id: 'r-2',
          source: 'efactura',
          invoiceNumber: 'FIRMIO 0051',
          party: 'Nordic Retail SRL',
          amount: 9_200,
          currency: 'RON',
          issueDate: '2026-03-02T00:00:00.000Z',
          dueDate: '2026-03-17T00:00:00.000Z',
          daysOverdue: 14,
        },
        {
          id: 'r-3',
          source: 'efactura',
          invoiceNumber: 'FIRMIO 0057',
          party: 'MARTINASYNC SRL',
          amount: 18_000,
          currency: 'RON',
          issueDate: '2026-03-24T00:00:00.000Z',
          dueDate: '2026-04-23T00:00:00.000Z',
          daysOverdue: 0,
        },
      ],
      otherCurrencies: [],
    },
    payables: {
      total: 12_400,
      count: 2,
      buckets: [
        { key: 'current', count: 2, amount: 12_400 },
        { key: 'd1_30', count: 0, amount: 0 },
        { key: 'd31_60', count: 0, amount: 0 },
        { key: 'd61_90', count: 0, amount: 0 },
        { key: 'd90plus', count: 0, amount: 0 },
      ],
      topParty: { name: 'Subcontractor Alfa SRL', amount: 8_900, count: 1, share: 72 },
      items: [
        {
          id: 'p-1',
          source: 'efactura',
          invoiceNumber: 'ALF-2231',
          party: 'Subcontractor Alfa SRL',
          amount: 8_900,
          currency: 'RON',
          issueDate: '2026-03-20T00:00:00.000Z',
          dueDate: '2026-04-19T00:00:00.000Z',
          daysOverdue: 0,
        },
        {
          id: 'p-2',
          source: 'invoice',
          invoiceNumber: 'CONT-88',
          party: 'Contabilitate Expert SRL',
          amount: 3_500,
          currency: 'RON',
          issueDate: '2026-03-28T00:00:00.000Z',
          dueDate: '2026-04-12T00:00:00.000Z',
          daysOverdue: 0,
        },
      ],
      otherCurrencies: [],
      scheduledNextMonth: {
        payments: { count: 3, total: 21_400 },
        receipts: { count: 1, total: 18_000 },
      },
    },
    vat: {
      applicable: true,
      period: {
        label: p.key,
        collected: round2(inflows * 0.18),
        deductible: round2(outflows * 0.12),
        balance: round2(inflows * 0.18 - outflows * 0.12),
      },
      month: {
        label: p.key,
        collected: round2(inflows * 0.18),
        deductible: round2(outflows * 0.12),
        balance: round2(inflows * 0.18 - outflows * 0.12),
      },
      quarter: {
        label: `${p.months[0].year}-Q${quarterOf(p.months[0].month)}`,
        collected: round2(inflows * 0.18 * 2.1),
        deductible: round2(outflows * 0.12 * 2.1),
        balance: round2((inflows * 0.18 - outflows * 0.12) * 2.1),
      },
      ...(p.kind === 'year'
        ? {
            quarters: [1, 2, 3, 4].map((q) => ({
              label: `${p.months[0].year}-Q${q}`,
              collected: round2(inflows * 0.045 * q),
              deductible: round2(outflows * 0.03 * q),
              balance: round2(inflows * 0.045 * q - outflows * 0.03 * q),
            })),
          }
        : {}),
    },
    dataQuality: {
      transactions: {
        total: Math.round(54 * scale),
        reconciled: Math.round(52 * scale),
        unmatched: 2,
        unmatchedAmount: 1_240,
        ignored: 3,
      },
      statements: { total: p.months.length, finalized: p.months.length, unfinalized: 0 },
      accountsWithoutStatement: 0,
      receivedInvoicesUnpaid: { count: 2, total: 12_400 },
      ledgerRows: Math.round(52 * scale),
      warnings: ['unmatched_lines'],
    },
    projects: {
      rows: [
        {
          projectId: 'proj-firmio',
          name: 'Firmio',
          code: 'FRM',
          revenue: round2(inflows * 0.52),
          expenses: round2(outflows * 0.3),
          margin: round2(inflows * 0.52 - outflows * 0.3),
          marginPct: 45.2,
          hours: Math.round(180 * scale),
          laborCost: round2(MONTHLY_PAYROLL * scale * 0.48),
          hoursWithoutRate: 0,
          marginWithLabor: round2(inflows * 0.52 - outflows * 0.3 - MONTHLY_PAYROLL * scale * 0.48),
          marginWithLaborPct: 18.6,
          budget: 480_000,
          budgetCurrency: 'RON',
          budgetUsedPct: round2((outflows * 0.3 + MONTHLY_PAYROLL * scale * 0.48) / 4800),
          isBarter: false,
        },
        {
          projectId: 'proj-nordic',
          name: 'Nordic Retail — platformă',
          code: 'NRD',
          revenue: round2(inflows * 0.29),
          expenses: round2(outflows * 0.22),
          margin: round2(inflows * 0.29 - outflows * 0.22),
          marginPct: 27.4,
          hours: Math.round(96 * scale),
          laborCost: round2(MONTHLY_PAYROLL * scale * 0.26),
          hoursWithoutRate: Math.round(12 * scale),
          marginWithLabor: round2(
            inflows * 0.29 - outflows * 0.22 - MONTHLY_PAYROLL * scale * 0.26,
          ),
          marginWithLaborPct: 4.1,
          budget: 120_000,
          budgetCurrency: 'EUR',
          budgetUsedPct: null,
          isBarter: false,
        },
        {
          projectId: 'proj-barter',
          name: 'Schimb servicii — Arcadia',
          code: null,
          revenue: 0,
          expenses: round2(outflows * 0.02),
          margin: round2(-outflows * 0.02),
          marginPct: null,
          hours: Math.round(14 * scale),
          laborCost: round2(MONTHLY_PAYROLL * scale * 0.04),
          hoursWithoutRate: 0,
          marginWithLabor: round2(-outflows * 0.02 - MONTHLY_PAYROLL * scale * 0.04),
          marginWithLaborPct: null,
          budget: null,
          budgetCurrency: 'RON',
          budgetUsedPct: null,
          isBarter: true,
        },
      ],
      unallocated: { revenue: round2(inflows * 0.19), expenses: round2(outflows * 0.46) },
      unallocatedExpensesShare: 46,
      laborCostTotal: round2(MONTHLY_PAYROLL * scale * 0.78),
      hoursTotal: Math.round(290 * scale),
      hoursWithoutRateTotal: Math.round(12 * scale),
      losingProjects: 1,
      warnings: ['labor_estimated', 'hours_without_rate', 'budget_other_currency'],
    },
    trends: {
      months: keys.map((period, i) => {
        const revenue = round2(MONTHLY_INFLOWS * scale * (0.8 + i * 0.03));
        const expenses = round2(MONTHLY_OUTFLOWS * scale * (0.85 + i * 0.02));
        const [y, rest] = period.split('-');
        return {
          period,
          year: Number(y),
          month: rest?.startsWith('Q') ? (Number(rest.slice(1)) - 1) * 3 + 1 : Number(rest ?? 1),
          revenue,
          expenses,
          net: round2(revenue - expenses),
          cumulativeNet: round2((revenue - expenses) * (i + 1)),
          balance: i % 4 === 1 ? null : round2(180_000 + i * 11_500),
        };
      }),
      bucketKind: p.kind === 'custom' ? 'month' : p.kind,
      averageExpenses3m: round2(MONTHLY_OUTFLOWS * 0.97),
      runwayMonths: 2.6,
    },
    ...(stale ? {} : { people: buildPeople(scale, inflows, outflows, keys) }),
    ...(stale ? {} : { clients: buildClients(scale, keys) }),
  };

  return {
    report,
    closing: stale
      ? {
          closed: true,
          closedAt: '2026-02-03T09:12:00.000Z',
          closedBy: { id: 'u-andrei', name: 'Andrei Pata' },
        }
      : { closed: false, closedAt: null, closedBy: null },
    fromSnapshot: stale,
  };
}

/** Lunile marcate ca închise în selectorul de lună. */
export function buildReportClosings(): ClosingListItem[] {
  return [{ year: 2026, month: 1, closedAt: '2026-02-03T09:12:00.000Z' }];
}
